(() => {
  'use strict';
  if (window.GasNeedsQuote) return;

  const storageKey = 'gasneeds:availability-basket:v1';
  const validHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const validQuantity = (value) => Number.isSafeInteger(value) && value >= 1 && value <= 10000;

  let nativeCount = Number(window.theme?.cartCount || 0);
  let fault = '';
  let cachedItems = [];

  function clean(item) {
    if (
      !item ||
      !validHandle(item.handle) ||
      !Number.isSafeInteger(Number(item.id)) ||
      Number(item.id) < 1 ||
      !validQuantity(Number(item.quantity))
    ) return null;

    let image = '';

    try {
      const url = new URL(String(item.image || ''), window.location.origin);
      if (item.image && url.protocol === 'https:') image = url.href;
    } catch (_) {
      /* Image is optional. */
    }

    return {
      id: Number(item.id),
      handle: item.handle,
      title: String(item.title || item.handle).slice(0, 500),
      variant: String(item.variant || '').replace(/^Default Title$/, '').slice(0, 250),
      sku: String(item.sku || '').slice(0, 250),
      quantity: Number(item.quantity),
      image
    };
  }

  function read() {
    try {
      const raw = localStorage.getItem(storageKey);
      const data = raw ? JSON.parse(raw) : { version: 1, items: [] };

      if (data.version !== 1 || !Array.isArray(data.items)) {
        throw new Error('Invalid saved basket');
      }

      const unique = new Map();

      data.items.forEach((item) => {
        const value = clean(item);

        if (!value) {
          throw new Error('Invalid saved product');
        }

        unique.set(value.id, value);
      });

      cachedItems = [...unique.values()];
      fault = '';
    } catch (_) {
      fault = 'This browser could not load saved availability enquiries. Please keep this page open and contact our team if the problem continues.';
    }

    return cachedItems.map((item) => ({ ...item }));
  }

  function productUrl(item) {
    const root = window.routes?.rootUrl || '/';
    return `${root}products/${item.handle}?variant=${item.id}`;
  }

  function makeMiniRow(item) {
    const row = document.createElement('div');
    row.className = 'mini-cart__line-item gn-mini-quote__row';

    const imageLink = document.createElement('a');
    imageLink.className = 'mini-cart__image-wrapper';
    imageLink.href = productUrl(item);
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      imageLink.append(image);
    } else {
      const mark = document.createElement('span');
      mark.className = 'gn-quote__placeholder';
      mark.textContent = 'GASNEEDS';
      imageLink.append(mark);
    }

    const info = document.createElement('div');
    info.className = 'gn-mini-quote__info';

    const title = document.createElement('a');
    title.className = 'mini-cart__product-title text--strong link';
    title.href = productUrl(item);
    title.textContent = item.title;
    info.append(title);

    [
      item.variant,
      item.sku && `SKU: ${item.sku}`,
      'Availability to confirm'
    ]
      .filter(Boolean)
      .forEach((text) => {
        const line = document.createElement('p');
        line.className = 'gn-mini-quote__detail';
        line.textContent = text;
        info.append(line);
      });

    const quantity = document.createElement('span');
    quantity.className = 'gn-mini-quote__quantity';
    quantity.textContent = `Qty: ${item.quantity}`;

    row.append(imageLink, info, quantity);

    return row;
  }

  function paintCount() {
    const count = nativeCount + cachedItems.reduce(
      (total, item) => total + item.quantity,
      0
    );

    document.querySelectorAll('.header__cart-count').forEach((badge) => {
      badge.textContent = String(count);
    });

    return count;
  }

  function syncMini(mini) {
    if (!mini) {
      document.querySelectorAll('.gn-mini-quote').forEach(syncMini);
      paintCount();
      return;
    }

    const marker = mini.querySelector('[data-gn-mini-native-count]');

    if (marker) {
      nativeCount = Number(marker.dataset.gnMiniNativeCount || 0);
    }

    const list = mini.querySelector('[data-gn-mini-inquiries]');

    if (list) {
      list.replaceChildren(...cachedItems.map(makeMiniRow));
    }

    const count = paintCount();
    const label = mini.querySelector('[data-gn-mini-count]');

    if (label) {
      label.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    }

    const empty = mini.querySelector('[data-gn-mini-empty]');

    if (empty) {
      empty.hidden = count > 0;
    }
  }

  function notify(external = false) {
    syncMini();

    document.dispatchEvent(
      new CustomEvent('gn:inquiries-changed', {
        detail: {
          external,
          error: fault
        }
      })
    );
  }

  function write(change) {
    const current = read();

    if (fault) {
      throw new Error(fault);
    }

    const next = change(current);

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          version: 1,
          items: next
        })
      );
    } catch (_) {
      throw new Error(
        'This browser could not save the enquiry. Your existing basket is unchanged. Please enable site storage or contact our team.'
      );
    }

    cachedItems = next;
    notify();
  }

  window.GasNeedsQuote = {
    getItems: read,
    getError: () => fault,
    productUrl,

    add(item) {
      const value = clean(item);

      if (!value) {
        throw new Error('Could not add this product. Please try again.');
      }

      let added = false;

      write((items) => {
        if (items.some((entry) => entry.id === value.id)) {
          return items;
        }

        added = true;
        return [...items, value];
      });

      return added;
    },

    setQuantity(id, quantity) {
      if (!validQuantity(quantity)) {
        throw new Error('Enter a whole quantity from 1 to 10,000.');
      }

      write((items) => {
        const item = items.find((entry) => entry.id === Number(id));

        if (!item) {
          throw new Error('This enquiry was removed in another tab.');
        }

        item.quantity = quantity;
        return items;
      });
    },

    remove(id) {
      write((items) =>
        items.filter((entry) => entry.id !== Number(id))
      );
    },

    setNativeCount(count) {
      nativeCount = Math.max(0, Number(count) || 0);

      if (window.theme) {
        window.theme.cartCount = nativeCount;
      }

      document
        .querySelectorAll('[data-gn-mini-native-count]')
        .forEach((marker) => {
          marker.dataset.gnMiniNativeCount = String(nativeCount);
        });

      syncMini();
    },

    syncMini
  };

  read();

  const boot = () => syncMini();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cart:rerendered', boot, true);
  document.addEventListener('shopify:section:load', boot);

  window.addEventListener('pageshow', () => {
    read();
    notify(true);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === null) {
      read();
      notify(true);
    }
  });
})();